import React, { useContext, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { BaseContext } from 'context/base.context';
import { RoutePath } from 'routes/route-path.constant';

import { UserVerificationKeys } from 'constants/user-verification';
import { LocalStorageKeys } from 'constants/local-storage';
import { APP_TYPE_APP, APP_TYPE_PWA } from 'constants/app-name';
import { ExperimentsKeys } from 'constants/experiments';

import { disableBrowserBackButton } from 'helpers/disable-browser-back-button';
import { detectIosDevice, isDesktop } from 'helpers/detect-device-type';

import { MobileContent } from 'pages/welcome/mobile-content';

import ableLogo from 'assets/images/svg/welcome/able-logo.svg';
import vibrantLogo from 'assets/images/svg/welcome/vibrant-logo.svg';
import qrCode from 'assets/images/png/welcome/qr-code.png';

import { useStyles } from './styles';

export const Welcome = () => {
    const { pageConfiguration } = useContext(BaseContext);

    disableBrowserBackButton(window.location.href);

    const { t } = useTranslation();

    const {
        logosWrapper,
        welcomeDesktopInfoWrapper,
        welcomeDesktopTitle,
        welcomeDesktopSubtitle,
        qrCodeImage,
        listWrapper,
        list,
        listTitle,
        listItem,
    } = useStyles();

    const email = localStorage.getItem(LocalStorageKeys[RoutePath.Email]) as string;
    const isUserActive = localStorage.getItem(UserVerificationKeys.isUserActive) === 'true';

    const pwaNativeSplitVariationExp = localStorage.getItem(ExperimentsKeys.pwaNativeSplitVariation) === '1';
    const userApp = pwaNativeSplitVariationExp ? APP_TYPE_PWA : APP_TYPE_APP;

    const handleClick = () => {
        const linkDownload = `https://install.ableapp.com/gdEx?deep_link_value=ableapp://email_login?email=${encodeURIComponent(email)}`;

        window.open(linkDownload, '_blank');

        pageConfiguration.handleOnPageComplete({ userApp });
    };

    useEffect(() => {
        const head = document.getElementsByTagName('head')[0];
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = 'https://banner.urlgeni.us/smart_banners/75f8f156e39bdf64.js?url=' + encodeURI(window.location.toString());
        head.appendChild(script);
    }, []);

    const isIosDevice = detectIosDevice();

    const appStoreLinkComponent = (
        <a href='https://apps.apple.com/us/app/vibrantlife-connect/id6451144527' target='_blank'
           rel='noreferrer nofollow'>
            AppStore
        </a>
    );
    const googlePlayLinkComponent = (
        <a href='https://play.google.com/store/apps/details?id=com.able.user' target='_blank' rel='noreferrer nofollow'>
            Google Play
        </a>
    );

    const ableAppName = t('welcome-instructions-title-personalized');

    return (
        <>
            {(isIosDevice || isDesktop()) && <div className={logosWrapper}>
                <img src={ableLogo} alt='Able Logo' />
                <img src={vibrantLogo} alt='Vibrant Logo' />
            </div>}

            {isDesktop() ? <>
                <h1 className={welcomeDesktopTitle}>
                    {!isUserActive && t('welcome-title')}
                    {isUserActive && t('welcome-title-if-user-exists')}
                </h1>
                <h2 className={welcomeDesktopSubtitle}>
                    {t('welcome-desktop-info')}
                </h2>

                <div className={welcomeDesktopInfoWrapper}>
                    <div className={listWrapper}>
                        <h2 className={listTitle}>
                            {!isUserActive && t('welcome-instructions-title', { ableAppName })}
                            {isUserActive && t('welcome-subtitle-if-user-exists')}
                        </h2>
                        <ol className={list}>
                            <h2 className={listItem}>
                                <li>
                                    {t('welcome-list-text-desktop-scan-qr')}
                                </li>
                            </h2>
                            <h2 className={listItem}>
                                <li>
                                    <Trans i18nKey='welcome-list-text-desktop-store-redirect'
                                           components={[appStoreLinkComponent, googlePlayLinkComponent]} />
                                </li>
                            </h2>
                            <h2 className={listItem}>
                                <li>
                                    {t('welcome-list-text-desktop-download-app')}
                                </li>
                            </h2>
                            <h2 className={listItem}>
                                <li>
                                    {!isUserActive && t('welcome-list-text-desktop-open-app', { email })}
                                    {isUserActive && t('welcome-list-text-desktop-open-app-if-user-exists', { email })}
                                </li>
                            </h2>

                            {!isUserActive &&
                                <h2 className={listItem}>
                                    <li>
                                        {t('welcome-list-text-desktop-create-password')}
                                    </li>
                                </h2>
                            }
                        </ol>
                    </div>

                    <img className={qrCodeImage} src={qrCode} alt='customer' />
                </div>
            </> : <MobileContent isIosDevice={isIosDevice} isUserActive={isUserActive} email={email}
                                 handleClick={handleClick} />}
        </>
    );
};
